// DEPCHECKUI1 (docs/build-briefs/DEPCHECKUI1-system-checks-screens.md; DEC-612). The words of the System checks card in
// Settings, the red strip and the software owner table. Pure functions, no DOM. The server writes one plain sentence per check
// (src/domain/dependencyChecks.ts); this adds the name of each check, the chip, what to do, and how the results are grouped.

import { MONTH_NAMES } from "./dateText.ts";
import type {
  LocationSystemChecksDto,
  SystemCheckDto,
  SystemCheckKeyDto,
  SystemCheckStatusDto,
} from "./api.ts";

/** The order the checks read in, the one the approved mockup shows. */
export const SYSTEM_CHECK_ORDER: readonly SystemCheckKeyDto[] = [
  "mytime_client_list",
  "mytime_connection",
  "card_reader",
  "card_payments",
  "background_jobs",
  "email_delivery",
  "tax_and_business_day",
  "product_lookup_ai",
];

export const SYSTEM_CHECK_NAMES: Readonly<Record<SystemCheckKeyDto, string>> = {
  mytime_client_list: "MyTime client list",
  mytime_connection: "MyTime connection",
  card_reader: "Card reader",
  card_payments: "Card payments",
  background_jobs: "Background jobs",
  email_delivery: "Email",
  tax_and_business_day: "Tax and business day",
  product_lookup_ai: "AI product lookup",
};

export const SYSTEM_CHECKS_TITLE = "System checks";
export const SYSTEM_CHECKS_HELP =
  "Each row is something this location needs to find clients and take card payments. A row that says Failing or Not set up needs someone to fix it.";
export const RUN_CHECKS_LABEL = "Run checks now";
export const RUNNING_CHECKS_LABEL = "Checking...";
export const OPEN_SYSTEM_CHECKS_LABEL = "Open System checks";
export const NO_CHECKS_LINE =
  "The first check runs within the hour. Run checks now to see it sooner.";
export const LOAD_FAILED_LINE = "System checks could not load.";
export const RUN_FAILED_LINE =
  "The checks could not be run just now. The results below are the last ones saved.";
export const NO_LOCATION_LINE =
  "This device is not set up as a location, so there are no system checks to show.";
export const STALE_LINE = "These results are over 3 hours old. Run the checks again.";

export interface ChipView {
  label: string;
  symbol: string;
  tone: "good" | "bad" | "warn" | "neutral";
}

const STATUS_CHIPS: Readonly<Record<SystemCheckStatusDto, ChipView>> = {
  working: { label: "Working", symbol: "✓", tone: "good" },
  failing: { label: "Failing", symbol: "!", tone: "bad" },
  not_set_up: { label: "Not set up", symbol: "-", tone: "warn" },
};

export function statusChip(status: SystemCheckStatusDto): ChipView {
  return STATUS_CHIPS[status];
}

/** What a person does about a check that is failing or not set up, one sentence, or "" when the sentence above already says. */
export function whatToDo(check: Pick<SystemCheckDto, "key" | "status">): string {
  if (check.status === "working") return "";
  switch (check.key) {
    case "mytime_client_list":
      return check.status === "failing"
        ? "Guests can still be added by name. Use Get help if this stays red."
        : "";
    case "mytime_connection":
      return "Snip & Sip sets this up. Use Get help to ask.";
    case "card_reader":
      return "";
    case "card_payments":
      return "Finish card setup in Settings, Tabs and payment.";
    case "background_jobs":
      return "Use Get help if this stays red.";
    case "email_delivery":
      return "Use Get help to ask Snip & Sip to set up email.";
    case "tax_and_business_day":
      return "Confirm the sales tax rate and the time zone and cutoff in Settings.";
    case "product_lookup_ai":
      return "Snip & Sip sets this up. Products can still be added by hand.";
  }
}

/** The checks in reading order. A card reader group keeps the server's order inside itself. */
export function orderedChecks(checks: readonly SystemCheckDto[]): SystemCheckDto[] {
  const rank = (key: SystemCheckKeyDto): number => SYSTEM_CHECK_ORDER.indexOf(key);
  return checks
    .map((check, index) => ({ check, index }))
    .sort((a, b) => rank(a.check.key) - rank(b.check.key) || a.index - b.index)
    .map((x) => x.check);
}

export function failingChecks(checks: readonly SystemCheckDto[]): SystemCheckDto[] {
  return orderedChecks(checks).filter((check) => check.status === "failing");
}

/** The summary beside the title: what needs attention first, then what is not set up, otherwise all working. */
export function summaryChip(
  view: Pick<LocationSystemChecksDto, "checks" | "failing" | "notSetUp"> | undefined,
): ChipView {
  if (view === undefined || view.checks.length === 0)
    return { label: "Not checked yet", symbol: "-", tone: "neutral" };
  if (view.failing > 0)
    return {
      label: `${view.failing} need${view.failing === 1 ? "s" : ""} attention`,
      symbol: "!",
      tone: "bad",
    };
  if (view.notSetUp > 0) return { label: `${view.notSetUp} not set up`, symbol: "-", tone: "warn" };
  return { label: "All working", symbol: "✓", tone: "good" };
}

const clockText = (when: Date): string =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(when);

/** "Checked 6:00 AM today", "Checked 6:00 AM yesterday", "Checked Oct 28, 6:00 AM". In the viewer's own zone, never UTC. */
export function checkedAtText(iso: string | null, now: Date): string {
  if (iso === null) return "Not checked yet";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "Not checked yet";
  const dayKey = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((dayKey(now) - dayKey(when)) / 86_400_000);
  if (days === 0) return `Checked ${clockText(when)} today`;
  if (days === 1) return `Checked ${clockText(when)} yesterday`;
  const month = MONTH_NAMES[when.getMonth()] ?? "";
  return `Checked ${month} ${when.getDate()}, ${clockText(when)}`;
}

/** The strip's sentence, or "" when nothing is failing. */
export function stripText(checks: readonly SystemCheckDto[]): string {
  const failing = failingChecks(checks);
  if (failing.length === 0) return "";
  const names: string[] = [];
  for (const check of failing) {
    const name = SYSTEM_CHECK_NAMES[check.key];
    if (!names.includes(name)) names.push(name);
  }
  const noun = failing.length === 1 ? "system check needs" : "system checks need";
  return `${failing.length} ${noun} attention: ${names.join(", ")}.`;
}

// ---------------------------------------------------------------------------------------------------- software owner table

export type OwnerGroupId = "mytime" | "readers" | "card_payments" | "jobs" | "email" | "tax_day";

export const OWNER_GROUPS: readonly {
  id: OwnerGroupId;
  label: string;
  keys: readonly SystemCheckKeyDto[];
}[] = [
  { id: "mytime", label: "MyTime", keys: ["mytime_client_list", "mytime_connection"] },
  { id: "readers", label: "Readers", keys: ["card_reader"] },
  { id: "card_payments", label: "Card payments", keys: ["card_payments"] },
  { id: "jobs", label: "Jobs", keys: ["background_jobs"] },
  { id: "email", label: "Email", keys: ["email_delivery"] },
  { id: "tax_day", label: "Tax and day", keys: ["tax_and_business_day"] },
];

/** One group's chip for one location: the worst status of its checks. Readers say how many are offline, or that there is none. */
export function ownerGroupChip(
  groupId: OwnerGroupId,
  checks: readonly Pick<SystemCheckDto, "key" | "status">[],
): ChipView {
  const group = OWNER_GROUPS.find((g) => g.id === groupId);
  const mine = checks.filter((check) => group?.keys.includes(check.key) === true);
  if (mine.length === 0) return { label: "Not checked", symbol: "-", tone: "neutral" };
  const failing = mine.filter((check) => check.status === "failing");
  if (groupId === "readers") {
    if (failing.length > 0) return { label: `${failing.length} offline`, symbol: "!", tone: "bad" };
    if (mine.every((check) => check.status === "not_set_up"))
      return { label: "No reader", symbol: "-", tone: "warn" };
    return statusChip("working");
  }
  if (failing.length > 0) return statusChip("failing");
  if (mine.some((check) => check.status === "not_set_up")) return statusChip("not_set_up");
  return statusChip("working");
}

export interface OwnerLocationRow {
  customerTenantId: string;
  customerName: string;
  locationId: string;
  locationName: string;
  checks: SystemCheckDto[];
  failing: number;
  notSetUp: number;
  stale: boolean;
}

/** Locations with a failing check first, then by customer and location name. */
export function sortOwnerRows<
  T extends Pick<OwnerLocationRow, "customerName" | "locationName" | "failing">,
>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.failing > 0) - Number(a.failing > 0) ||
      a.customerName.localeCompare(b.customerName) ||
      a.locationName.localeCompare(b.locationName),
  );
}

export function ownerSummaryChip(rows: readonly Pick<OwnerLocationRow, "failing">[]): ChipView {
  if (rows.length === 0) return { label: "Nothing checked yet", symbol: "-", tone: "neutral" };
  const failing = rows.filter((row) => row.failing > 0).length;
  if (failing > 0)
    return {
      label: `${failing} location${failing === 1 ? "" : "s"} failing`,
      symbol: "!",
      tone: "bad",
    };
  return { label: "All locations working", symbol: "✓", tone: "good" };
}

// ------------------------------------------------------------------------------------------- software owner home strip

/** SYSHOME1: the console pages the red strip may be on. Only the home page ("dashboard") is on the list. */
export const OWNER_STRIP_VIEWS: readonly string[] = ["dashboard"];

/** The software owner strip's sentence, or "" when no location has a failing check. */
export function ownerStripText(summary: { failing: number; locationsWithFailures: number }): string {
  if (summary.failing <= 0 || summary.locationsWithFailures <= 0) return "";
  const where = summary.locationsWithFailures === 1 ? "1 location has" : `${summary.locationsWithFailures} locations have`;
  const what = summary.failing === 1 ? "a system check that needs" : `${summary.failing} system checks that need`;
  return `${where} ${what} attention.`;
}

/** The strip shows on the console home page only, and only while something fails. */
export function ownerStripVisible(
  view: string,
  summary: { failing: number; locationsWithFailures: number },
): boolean {
  return OWNER_STRIP_VIEWS.includes(view) && ownerStripText(summary) !== "";
}
